import React, { useEffect, useRef, useContext } from 'react';
import * as d3 from 'd3';
import * as S from '../BarChart/BarChart.style';
import { useStore } from '../../../store/useStore';
import { ThemeContext } from 'styled-components';

type Props = {
  size: { width: number; height: number };
};

export const Reachability = ({ size }: Props) => {
  const dataFeed = useStore((state) => state.dataFeed);
  const reachability = dataFeed.reachability;
  const theme = useContext(ThemeContext);
  const chartRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!theme || !chartRef.current) return;
    if (!size.width || !size.height || size.width <= 0 || size.height <= 0) return;

    const levels = reachability?.levels ?? [];

    const labels = levels.map((l) => `P(|1H return| \u2265 ${Math.abs(l.movePct)}%)`);
    const values = levels.map((l) => l.probPct);
    const maxLabelLength = labels.reduce((max, label) => Math.max(max, label.length), 0);
    const leftMargin = Math.min(150, Math.max(78, maxLabelLength * 6 + 14));
    const margin = { top: 12, right: 40, bottom: 32, left: leftMargin };
    const width = size.width - margin.left - margin.right;
    const height = size.height - margin.top - margin.bottom;

    if (width <= 0 || height <= 0) return;

    d3.select('#reachability').selectAll('svg').remove();

    const svg = d3
      .select('#reachability')
      .append('svg')
      .attr('width', size.width)
      .attr('height', size.height)
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // X scale: probability 0-100
    const x = d3.scaleLinear().domain([0, 100]).range([0, width]);

    // Y scale: one band per threshold
    const y = d3
      .scaleBand<string>()
      .domain(labels)
      .range([0, height])
      .padding(0.35);

    // X axis
    svg
      .append('g')
      .attr('transform', `translate(0,${height})`)
      .call(d3.axisBottom(x).ticks(5).tickFormat((d) => `${d}%`))
      .selectAll('text')
      .attr('fill', theme.text);

    // Y axis
    const yAxis = svg.append('g').call(d3.axisLeft(y).tickSize(0));
    yAxis
      .selectAll('text')
      .attr('fill', theme.text)
      .attr('text-anchor', 'start')
      .attr('x', -margin.left + 8);

    // Style axis lines
    svg.selectAll('.domain, .tick line').attr('stroke', theme.text).attr('opacity', 0.3);

    // Bars
    svg
      .selectAll('rect')
      .data(values)
      .enter()
      .append('rect')
      .attr('x', 0)
      .attr('y', (_d, i) => y(labels[i]) ?? 0)
      .attr('width', (d) => x(d))
      .attr('height', y.bandwidth())
      .attr('fill', theme.primary);

    // Value labels at end of each bar
    svg
      .selectAll('.bar-label')
      .data(values)
      .enter()
      .append('text')
      .attr('x', (d) => x(d) + 6)
      .attr('y', (_d, i) => (y(labels[i]) ?? 0) + y.bandwidth() / 2)
      .attr('dy', '0.35em')
      .attr('fill', theme.text)
      .attr('opacity', 0.72)
      .style('font-size', '12px')
      .text((d) => `${d}%`);
  }, [reachability, size, theme]);

  return (
    <S.ChartSpace style={{ position: 'relative', overflow: 'hidden' }}>
      <div
        ref={chartRef}
        id="reachability"
        style={{ position: 'absolute', top: 0, left: 0 }}
      ></div>
    </S.ChartSpace>
  );
};
